import React from "react";
import {
  Check,
  Minus,
  Percent,
  Plus,
  Receipt,
  Tag,
  Trash2,
  X,
} from "lucide-react";

const choiceClass = (selected) =>
  `relative flex min-h-[76px] items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition ${
    selected
      ? "border-amber-300 bg-amber-400/10 text-white"
      : "border-white/10 bg-white/5 text-white/75 hover:border-white/20"
  }`;

export function DiscountModal({
  open,
  tableLabel,
  customerCount,
  items,
  adjustments,
  draft,
  setDraft,
  canApply,
  formatPrice,
  formatItemName,
  computeItemTotal,
  getItemKey,
  onOpenItem,
  onRemove,
  onRemoveAll,
  onApplyGlobal,
  onClose,
  wholeLabel = "Toute l’addition",
  globalDisabledReason = "",
}) {
  if (!open) return null;

  const subtotal = items.reduce(
    (sum, item) => sum + (Number(computeItemTotal(item)) || 0),
    0,
  );
  const discountTotal = adjustments.reduce(
    (sum, adjustment) => sum + (Number(adjustment?.amount) || 0),
    0,
  );
  const totalAfter = Math.max(0, subtotal - discountTotal);
  const cartDiscount = adjustments.find(
    (adjustment) => adjustment?.type === "discount" && adjustment?.scope === "cart",
  );
  const itemDiscounts = adjustments.filter(
    (adjustment) => adjustment?.type === "discount" && adjustment?.scope === "item",
  );
  const isCartMode = draft.scope !== "item";
  const rawValue = Number(draft.value);
  const value = Number.isFinite(rawValue) ? rawValue : 0;
  const previewDiscount = Math.max(
    0,
    draft.unit === "percentage" ? (subtotal * value) / 100 : value,
  );
  const previewTotal = Math.max(0, subtotal - previewDiscount);
  const globalBlocked = itemDiscounts.length > 0;
  const globalValid =
    canApply &&
    !globalBlocked &&
    value > 0 &&
    String(draft.reason || "").trim().length > 0 &&
    (draft.unit !== "percentage" || value <= 100) &&
    (draft.unit === "percentage" || value <= subtotal);
  const rawTableLabel = String(tableLabel || "Addition").trim();
  const tableText = /^table\b/i.test(rawTableLabel)
    ? rawTableLabel.replace(/^table\s*/i, "Table ")
    : /^\d+$/.test(rawTableLabel)
      ? `Table ${rawTableLabel}`
      : rawTableLabel;
  const showGlobalPreview = isCartMode && value > 0 && !globalBlocked;
  const displayedTotal = showGlobalPreview ? previewTotal : totalAfter;
  const displayedDiscount = showGlobalPreview ? previewDiscount : discountTotal;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Faire une réduction"
    >
      <div className="relative flex max-h-[94dvh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0b1328] via-[#0f1c36] to-[#0b1328] text-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-white/10 bg-white/5 px-5 py-4 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-amber-300">
              <Percent size={24} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-amber-200">
                {tableText}{customerCount ? ` · ${customerCount} personne${customerCount > 1 ? "s" : ""}` : ""}
              </p>
              <h3 className="text-2xl font-bold leading-tight">Faire une réduction</h3>
              <p className="text-sm text-white/65">Addition actuelle : {formatPrice(subtotal)}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-white/10 bg-white/10 p-3 hover:bg-white/20"
            aria-label="Fermer"
          >
            <X size={19} />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          <section className="space-y-3">
            <h4 className="font-semibold">Réduire quoi ?</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                className={choiceClass(isCartMode)}
                onClick={() => setDraft((previous) => ({ ...previous, scope: "cart" }))}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-300/10 text-amber-200">
                  <Receipt size={21} />
                </span>
                <span className="font-semibold">{wholeLabel}</span>
                {isCartMode && (
                  <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-slate-950">
                    <Check size={13} strokeWidth={3} />
                  </span>
                )}
              </button>
              <button
                type="button"
                className={choiceClass(!isCartMode)}
                onClick={() => setDraft((previous) => ({ ...previous, scope: "item" }))}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white/75">
                  <Tag size={21} />
                </span>
                <span className="font-semibold">Article par article</span>
                {!isCartMode && (
                  <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-slate-950">
                    <Check size={13} strokeWidth={3} />
                  </span>
                )}
              </button>
            </div>
          </section>

          {isCartMode ? (
            <section className="space-y-4">
              {globalDisabledReason && (
                <p role="alert" className="rounded-2xl border border-amber-300/35 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
                  {globalDisabledReason}
                </p>
              )}
              {globalBlocked ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300/35 bg-amber-400/10 px-4 py-3">
                  <p className="text-sm text-amber-100">
                    Retirez d’abord les réductions par article.
                  </p>
                  <button
                    type="button"
                    onClick={() => onRemoveAll ? onRemoveAll() : itemDiscounts.forEach((adjustment) => onRemove(adjustment.id))}
                    className="rounded-xl bg-amber-400 px-3 py-2 text-sm font-semibold text-slate-950"
                  >
                    Tout retirer
                  </button>
                </div>
              ) : (
                <>
                  {cartDiscount && (
                    <div className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3">
                      <div>
                        <p className="font-semibold text-emerald-100">Réduction actuelle : -{formatPrice(cartDiscount.amount)}</p>
                        <p className="text-sm text-white/60">{cartDiscount.reason}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemove(cartDiscount.id)}
                        className="rounded-xl border border-red-300/30 bg-red-500/10 p-2 text-red-200"
                        aria-label="Retirer la réduction"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="font-semibold">Montant de la réduction</label>
                    <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-3">
                      <div className="grid grid-cols-2 rounded-2xl border border-white/10 bg-white/5 p-1">
                        {[
                          ["amount", "F"],
                          ["percentage", "%"],
                        ].map(([unit, label]) => (
                          <button
                            key={unit}
                            type="button"
                            onClick={() => setDraft((previous) => ({ ...previous, unit }))}
                            className={`rounded-xl font-bold ${draft.unit === unit ? "border border-amber-300 bg-amber-400/15 text-amber-100" : "text-white/60"}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <input
                        type="number"
                        min="0"
                        max={draft.unit === "percentage" ? 100 : subtotal}
                        step="0.01"
                        value={draft.value}
                        onChange={(event) => setDraft((previous) => ({ ...previous, value: event.target.value }))}
                        className="min-h-[58px] w-full rounded-2xl border border-amber-300/50 bg-slate-950/45 px-4 text-2xl font-bold outline-none focus:ring-2 focus:ring-amber-300/40"
                        placeholder="0"
                        autoComplete="off"
                        autoFocus
                      />
                    </div>
                    {draft.unit === "percentage" && (
                      <div className="flex flex-wrap gap-2">
                        {[5, 10, 15, 20].map((percentage) => (
                          <button
                            key={percentage}
                            type="button"
                            onClick={() => setDraft((previous) => ({ ...previous, value: String(percentage) }))}
                            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm hover:border-amber-300/50"
                          >
                            {percentage} %
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="global-discount-reason" className="font-semibold">Motif</label>
                    <textarea
                      id="global-discount-reason"
                      rows={2}
                      value={draft.reason}
                      onChange={(event) => setDraft((previous) => ({ ...previous, reason: event.target.value }))}
                      className="w-full resize-none rounded-2xl border border-white/10 bg-slate-950/45 px-4 py-3 outline-none focus:ring-2 focus:ring-amber-300/40"
                      placeholder="Saisir le motif de la réduction"
                    />
                  </div>

                  {value > 0 && (
                    <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                      <span className="text-white/65">Le client paiera</span>
                      <strong className="text-xl text-amber-200">{formatPrice(previewTotal)}</strong>
                    </div>
                  )}
                </>
              )}
            </section>
          ) : (
            <section className="space-y-3">
              {cartDiscount ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300/35 bg-amber-400/10 px-4 py-3">
                  <p className="text-sm text-amber-100">Retirez d’abord la réduction sur toute l’addition.</p>
                  <button
                    type="button"
                    onClick={() => onRemove(cartDiscount.id)}
                    className="rounded-xl bg-amber-400 px-3 py-2 text-sm font-semibold text-slate-950"
                  >
                    Retirer
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {items.map((item, index) => {
                    const itemKey = getItemKey(item, index);
                    const adjustment = itemDiscounts.find(
                      (entry) => String(entry.targetItemId) === String(itemKey),
                    );
                    return (
                      <div
                        key={itemKey}
                        className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${adjustment ? "border-emerald-400/30 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}
                      >
                        <div className="min-w-0">
                          <p className="truncate font-semibold">{formatItemName(item)}</p>
                          <p className="text-sm text-white/60">
                            {Number(item.quantity) || 1} × {formatPrice(computeItemTotal({ ...item, quantity: 1 }))}
                          </p>
                          {adjustment && (
                            <p className="mt-1 text-sm text-emerald-200">
                              -{formatPrice(adjustment.amount)} · {adjustment.reason}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {adjustment && (
                            <button
                              type="button"
                              onClick={() => onRemove(adjustment.id)}
                              className="rounded-xl border border-red-300/30 bg-red-500/10 p-3 text-red-200"
                              aria-label="Retirer la réduction"
                            >
                              <Trash2 size={17} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => onOpenItem(item, adjustment)}
                            className="rounded-xl border border-amber-300/40 bg-amber-400/10 px-4 py-3 font-semibold text-amber-100"
                          >
                            {adjustment ? "Modifier" : "Réduire"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 bg-white/5 px-5 py-4 sm:px-6">
          <div>
            <p className="text-sm text-white/60">À payer</p>
            <p className="text-2xl font-bold text-amber-200">{formatPrice(displayedTotal)}</p>
            {displayedDiscount > 0 && <p className="text-xs text-emerald-200">Réduction : -{formatPrice(displayedDiscount)}</p>}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 font-semibold"
            >
              {isCartMode ? "Annuler" : "Terminer"}
            </button>
            {isCartMode && (
              <button
                type="button"
                onClick={onApplyGlobal}
                disabled={!globalValid}
                className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-5 py-3 font-bold text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Check size={18} /> Appliquer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ItemDiscountModal({
  open,
  draft,
  setDraft,
  error,
  formatPrice,
  formatItemName,
  computeItemTotal,
  onApply,
  onClose,
}) {
  if (!open || !draft.item) return null;
  const maxQuantity = Number(draft.item.quantity) || 1;
  const quantity = Math.min(Math.max(1, Number(draft.quantity) || 1), maxQuantity);
  const unitPrice = Number(computeItemTotal({ ...draft.item, quantity: 1 })) || 0;
  const rawValue = Number(draft.value);
  const value = Number.isFinite(rawValue) ? rawValue : 0;
  const reduction = draft.unit === "percentage"
    ? (unitPrice * quantity * value) / 100
    : value * quantity;
  const finalTotal = Math.max(0, unitPrice * quantity - reduction);
  const valid = value > 0 && String(draft.reason || "").trim() &&
    (draft.unit !== "percentage" || value <= 100) &&
    (draft.unit === "percentage" || value <= unitPrice);
  const changeQuantity = (next) => setDraft((previous) => ({
    ...previous,
    quantity: Math.min(maxQuantity, Math.max(1, next)),
  }));

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Réduction article">
      <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-white/10 bg-white/5 px-5 py-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-amber-200">Réduire cet article</p>
            <h3 className="text-xl font-bold">{formatItemName(draft.item)}</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/10 bg-white/10 p-2" aria-label="Fermer">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div className="flex items-center justify-between gap-4">
            <label className="font-semibold">Quantité</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => changeQuantity(quantity - 1)} disabled={quantity <= 1} className="rounded-full border border-white/10 bg-white/10 p-3 disabled:opacity-30" aria-label="Diminuer la quantité"><Minus size={18} /></button>
              <span className="min-w-[78px] rounded-xl border border-amber-300/40 bg-white/10 px-3 py-3 text-center font-bold">{quantity} sur {maxQuantity}</span>
              <button type="button" onClick={() => changeQuantity(quantity + 1)} disabled={quantity >= maxQuantity} className="rounded-full bg-amber-400 p-3 text-slate-950 disabled:opacity-30" aria-label="Augmenter la quantité"><Plus size={18} /></button>
            </div>
          </div>
          <div className="space-y-2">
            <label className="font-semibold">Montant de la réduction</label>
            <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-3">
              <div className="grid grid-cols-2 rounded-2xl border border-white/10 bg-white/5 p-1">
                {[["amount", "F"], ["percentage", "%"]].map(([unit, label]) => (
                  <button key={unit} type="button" onClick={() => setDraft((previous) => ({ ...previous, unit }))} className={`rounded-xl font-bold ${draft.unit === unit ? "border border-amber-300 bg-amber-400/15 text-amber-100" : "text-white/60"}`}>{label}</button>
                ))}
              </div>
              <input type="number" min="0" max={draft.unit === "percentage" ? 100 : unitPrice} step="0.01" value={draft.value} onChange={(event) => setDraft((previous) => ({ ...previous, value: event.target.value }))} className="min-h-[58px] rounded-2xl border border-amber-300/50 bg-slate-950/45 px-4 text-2xl font-bold outline-none" placeholder="0" />
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="item-discount-reason" className="font-semibold">Motif</label>
            <textarea id="item-discount-reason" rows={2} value={draft.reason} onChange={(event) => setDraft((previous) => ({ ...previous, reason: event.target.value }))} className="w-full resize-none rounded-2xl border border-white/10 bg-slate-950/45 px-4 py-3 outline-none focus:ring-2 focus:ring-amber-300/40" placeholder="Saisir le motif de la réduction" />
          </div>
          {value > 0 && (
            <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <span className="text-white/65">Nouveau total</span>
              <strong className="text-xl text-amber-200">{formatPrice(finalTotal)}</strong>
            </div>
          )}
          {error && <p role="alert" className="rounded-xl border border-red-400/40 bg-red-950/70 px-4 py-3 text-sm text-red-100">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 border-t border-white/10 bg-white/5 px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-xl border border-white/10 bg-white/5 px-5 py-3 font-semibold">Annuler</button>
          <button type="button" onClick={onApply} disabled={!valid} className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 px-5 py-3 font-bold text-slate-950 disabled:opacity-40"><Check size={18} /> Appliquer</button>
        </div>
      </div>
    </div>
  );
}
